import * as z from 'zod/v4';

import {
  type Board,
  type Coupon,
  isSportkryssValue,
  numberOfDrawsSchema,
  type SportkryssCoupon,
} from '../coupon/entry.js';
import { drawNumberSchema } from '../draw/entry.js';
import { currencySchema } from '../lib/entry.js';
import { couponProductSchema, productSchema } from '../product/entry.js';
import { isRSYS, isUSYS } from '../system/entry.js';
import { type BetProp, betPropsEnum } from './betProps.js';
import {
  externalSystemsOneXTwoBoardSchema,
  oneXTwoBoardSchema,
  scoreBoardSchema,
  sportkryssBoardSchema,
} from './board.js';

type CouponType = 'PRIMARY_BET' | 'PRIMARY_MULTI_BET' | 'ADDON_BET';
type ExtendedBetType = {
  betProperties: BetProp[];
};

export type BetSchemaType = { betInfo: ExtendedBetType; type: CouponType } & (
  | { type: Extract<CouponType, 'PRIMARY_BET' | 'PRIMARY_MULTI_BET'>; coupon: Coupon }
  | { type: Extract<CouponType, 'ADDON_BET'>; coupon: SportkryssCoupon }
);

const countNumberOfSrow = (boards: Board[]): number => {
  return boards.reduce(
    (acc, board) =>
      acc + board.outcomes.reduce((acc1, outcome) => acc1 * outcome.reduce((acc2, value) => acc2 * value.length, 1), 1),
    0
  );
};

const betBaseSchema = z.object({
  // productId: z.NEVER, // Needs to be overridden by each primary schema
  drawNum: drawNumberSchema,
  numDraws: numberOfDrawsSchema.default(1),
  betProperties: z.array(betPropsEnum),
  betAmountSalesGroup1: currencySchema,
  betAmountSalesGroup2: currencySchema,
  rowPriceMulti: z.number(),
});

const game1X2BoardsSchema = z.array(oneXTwoBoardSchema).transform((game1X2Boards) => {
  const uRowBoardIndex = game1X2Boards.findIndex(({ boardInfo }) => boardInfo.isURow);

  // If UROW is present make an assumption that there is only a main board left
  // and merge the UROW with the main bet
  if (uRowBoardIndex !== -1) {
    const [uRowBoard] = game1X2Boards.splice(uRowBoardIndex, 1);
    const [mainBoard] = game1X2Boards;

    if (mainBoard && uRowBoard) {
      mainBoard.board.uOutcomes = uRowBoard.board.uOutcomes;
    }
  }

  return game1X2Boards;
});

export const oneXTwoBetSchema = z
  .object({
    ...betBaseSchema.shape,
    game1X2Boards: game1X2BoardsSchema,
    productId: couponProductSchema,
  })
  .transform(({ game1X2Boards, drawNum, numDraws, rowPriceMulti, productId, betProperties }, ctx) => {
    const [firstBoard] = game1X2Boards;
    const boards: Board[] = [];

    if (!firstBoard) {
      ctx.addIssue({
        code: 'custom',
        message: 'No boards found',
      });

      return z.NEVER;
    }

    // Pick system info from first board
    // For now it isn't possible to have different system types on the boards
    const { systemType, systemNumber } = firstBoard.boardInfo;

    for (const { board } of game1X2Boards) {
      boards.push(board);
    }

    const rSystem = systemType === 'RSYS' && isRSYS(systemNumber) ? systemNumber : undefined;
    const uSystem = systemType === 'USYS' && isUSYS(systemNumber) ? systemNumber : undefined;

    const coupon: Coupon = {
      selectedSystem: systemType,
      lastSelectedSystemType: undefined,
      betAmount: rowPriceMulti,
      week: numDraws,
      // Default to 20 since reducera fritt is only present in egnaRaderOneXTwoBetSchema
      reduceModeCount: 20,
      drawNumber: drawNum,
      couponType: 'standard',
      product: productId,
      rSystem,
      uSystem,
      boards,
      // Sportkryss is a separate bet and will be updated one level up
      sportkryss: 0,
      sportkryssNumber: '0',
    } as const;

    const betInfo: ExtendedBetType = {
      betProperties,
    } as const;

    return { type: 'PRIMARY_BET', coupon, betInfo } as const satisfies BetSchemaType;
  });

export const externalSystemsOneXTwoBetSchema = z
  .object({
    ...betBaseSchema.shape,
    er1X2BoardData: externalSystemsOneXTwoBoardSchema,
    productId: couponProductSchema,
  })
  .transform(({ er1X2BoardData, betProperties, rowPriceMulti, numDraws, drawNum, productId }) => {
    const { systemType } = er1X2BoardData.boardInfo;

    const coupon: Coupon = {
      selectedSystem: systemType,
      lastSelectedSystemType: undefined,
      betAmount: rowPriceMulti,
      week: numDraws,
      reduceModeCount: countNumberOfSrow(er1X2BoardData.boards),
      drawNumber: drawNum,
      couponType: 'external_system',
      product: productId,
      rSystem: undefined,
      uSystem: undefined,
      boards: er1X2BoardData.boards,
      // Sportkryss not available for ER
      sportkryss: 0,
      sportkryssNumber: '0',
    } as const;

    const betInfo: ExtendedBetType = {
      betProperties,
    } as const;

    return { type: 'PRIMARY_MULTI_BET', coupon, betInfo } as const satisfies BetSchemaType;
  });

export const scoreBoardBetSchema = z.object({
  ...betBaseSchema.shape,
  // TODO: Should be implemented and transformed in the same way as eg. oneXTwoBetSchema
  scoreBoards: z.array(scoreBoardSchema),
  productId: couponProductSchema,
});

export const sportkryssBetSchema = z
  .object({
    ...betBaseSchema.shape,
    jokerBoards: z.array(sportkryssBoardSchema),
    productId: productSchema,
  })
  .transform(({ jokerBoards, drawNum: drawNumber, betAmountSalesGroup1 }, ctx) => {
    const [firstSportkryssBoard] = jokerBoards;

    if (!firstSportkryssBoard) {
      ctx.addIssue({
        code: 'custom',
        message: 'No sportkryss board found',
      });

      return z.NEVER;
    }

    const sportkryssValue = betAmountSalesGroup1 / 10;

    if (!isSportkryssValue(sportkryssValue)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid sportkryss value',
      });

      return z.NEVER;
    }

    const sportkryssCoupon: SportkryssCoupon = {
      sportkryssNumber: firstSportkryssBoard,
      sportkryss: sportkryssValue,
      drawNumber,
    } as const;

    const betInfo: ExtendedBetType = {
      betProperties: [],
    } as const;

    return { type: 'ADDON_BET', coupon: sportkryssCoupon, betInfo } as const satisfies BetSchemaType;
  });
